import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWebOrigin } from '../../utils/helpers';
import toast from 'react-hot-toast';
import { ArrowLeft, Loader, Trophy, Gamepad2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import GamesList from '../../components/common/GamesList';
import { eventsAPI, tournamentAPI } from '../../services/api';
import { getScoringComponent, getSportNameSync } from '../../scoring/scoringComponents';
import GameComponent from '../../scoring/GameComponent';
import { apiCall } from '../../services/apiUtil';
import { getCurrentUser } from '../../profile/services';
import TournamentCard from '../../tournaments/components/TournamentCard';
import { transformTournamentData } from '../../tournaments/utils/tournamentHelpers';
import { isSquadManager } from '../utils/squadRoles';

/**
 * PastGamesTab - Self-Contained Past Games Management Component
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance (required)
 *
 * Features:
 * - View list of past games with pagination
 * - Click on a game to view detailed scoring
 * - Sport-specific scoring displays (Poker, Cricket, Ping Pong)
 * - Share scorecard to group posts
 * - Delete games
 * - Copy game links
 *
 * Architecture:
 * - This component only manages the list/detail view toggle
 * - ScoringComponents fetch their own game data (self-fetching pattern)
 * - PastGamesTab just passes event data to ScoringComponents
 */

const PastGamesTab = ({ voProvider }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Local state for past games list
  const [pastGames, setPastGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // Local state for past (completed) tournaments owned by this squad
  const [pastTournaments, setPastTournaments] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(false);

  // Games vs Tournaments sub-tab. Starts undecided (null) — once both lists
  // have loaded, we pick a smart default: hide the toggle entirely if only
  // one category has data, otherwise default to whichever has the more
  // recent activity. Only used to seed the default; user's own click always wins.
  const [activeSubTab, setActiveSubTab] = useState(null);
  const [gamesFetched, setGamesFetched] = useState(false);
  const [tournamentsFetched, setTournamentsFetched] = useState(false);
  const [latestGameDate, setLatestGameDate] = useState(null); // 'YYYY-MM-DD'
  const [latestTournamentDate, setLatestTournamentDate] = useState(null); // ISO date string

  // Local state for selected game (detail view)
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedGameEvent, setSelectedGameEvent] = useState(null);

  const currentUserId = getCurrentUser()?.id;

  // Extract sport type before guard (optional-chaining so it's safe even without voProvider)
  const group = voProvider?.getGroup();
  const isAdmin = isSquadManager(group?.userRole);
  const groupId = voProvider?.vo?.groupId;
  const members = voProvider?.getMembers();
  const sportTypeId = group?.sportTypeId || group?.sportType?.id;

  // Resolved scoring component — undefined = loading, null = not found, fn = ready
  // getScoringComponent is async, so we resolve it in a useEffect and store in state.
  const [ScoringComponent, setScoringComponent] = useState(undefined);
  useEffect(() => {
    if (!sportTypeId) { setScoringComponent(null); return; }
    setScoringComponent(undefined);
    getScoringComponent(sportTypeId, 'pastGames').then(component => {
      // Wrap in arrow fn so React doesn't treat it as a state-updater fn
      setScoringComponent(component ? () => component : null);
    });
  }, [sportTypeId]);

  // Guard: Return loading state if voProvider not available
  if (!voProvider) {
    return (
      <div className={`${theme.card} rounded-lg p-8 text-center`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
        </div>
      </div>
    );
  }

  /**
   * Load past games from API
   */
  const loadPastGames = useCallback(async () => {
    if (!groupId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await eventsAPI.getMyGames({
        limit: pagination.limit,
        offset: pagination.offset,
        groupId: groupId,
        includeAllGroupGames: true
      });

      const games = result?.games || [];
      setPastGames(games);
      setPagination(prev => ({
        ...prev,
        total: result?.pagination?.total || 0,
        hasMore: result?.pagination?.hasMore || false
      }));
      setLatestGameDate(games.reduce((max, g) => (g.date && (!max || g.date > max) ? g.date : max), null));
    } catch (err) {
      console.error('Error loading past games:', err);
      setError('Failed to load past games. Please try again.');
    } finally {
      setLoading(false);
      setGamesFetched(true);
    }
  }, [groupId, pagination.limit, pagination.offset]);

  /**
   * Load past games when component mounts or pagination changes
   */
  useEffect(() => {
    loadPastGames();
  }, [loadPastGames]);

  /**
   * Load completed tournaments owned by this squad
   */
  const loadPastTournaments = useCallback(async () => {
    if (!groupId) return;

    setTournamentsLoading(true);
    try {
      const result = await tournamentAPI.getAll({
        ownerType: 'group',
        ownerId: groupId,
        status: 'completed',
        limit: 50
      });
      const raw = result?.data || [];
      setPastTournaments(raw.map(transformTournamentData));
      setLatestTournamentDate(raw.reduce((max, t) => (
        t.endDate && (!max || new Date(t.endDate) > new Date(max)) ? t.endDate : max
      ), null));
    } catch (err) {
      console.error('[PastGamesTab] Error loading past tournaments:', err);
    } finally {
      setTournamentsLoading(false);
      setTournamentsFetched(true);
    }
  }, [groupId]);

  useEffect(() => {
    loadPastTournaments();
  }, [loadPastTournaments]);

  // Smart default for the Games/Tournaments toggle: only decide once both
  // lists have loaded, and only ever set it once (a user's manual click
  // must never be overridden by this effect re-running).
  useEffect(() => {
    if (activeSubTab !== null) return;
    if (!gamesFetched || !tournamentsFetched) return;

    const hasGames = pastGames.length > 0;
    const hasTournaments = pastTournaments.length > 0;

    if (hasGames && hasTournaments) {
      const tournamentIsMoreRecent = latestTournamentDate && latestGameDate
        ? new Date(latestTournamentDate) > new Date(latestGameDate)
        : !!latestTournamentDate && !latestGameDate;
      setActiveSubTab(tournamentIsMoreRecent ? 'tournaments' : 'games');
    } else if (hasTournaments) {
      setActiveSubTab('tournaments');
    } else {
      setActiveSubTab('games');
    }
  }, [activeSubTab, gamesFetched, tournamentsFetched, pastGames, pastTournaments, latestGameDate, latestTournamentDate]);

  const handleTournamentClick = useCallback((tournament) => {
    navigate(`/tournament/${tournament.id}?tab=details`, {
      state: {
        tournament,
        fromSquad: { id: groupId, name: group?.name || 'Squad' }
      }
    });
  }, [navigate, groupId, group?.name]);

  /**
   * Handle clicking on a past game to view details
   * ScoringComponent will fetch its own game data
   */
  const handleGameClick = useCallback((game) => {
    // Create event object from game data
    const eventData = {
      id: game.id,
      ownerType: game.ownerType || 'group',
      ownerId: game.ownerId || groupId,
      subject: game.subject,
      date: game.date,
      startTime: game.startTime,
      endTime: game.endTime,
      venue: game.venue || null,
      createdBy: game.createdBy,
      status: game.eventStatus || game.status, // Use eventStatus field if available
      responses: game.responses || []
    };

    setSelectedGame(game);
    setSelectedGameEvent(eventData);
  }, [groupId]);

  /**
   * Handle going back to past games list
   */
  const handleBackToList = useCallback(() => {
    setSelectedGame(null);
    setSelectedGameEvent(null);
    voProvider.resetCurrentEvent();
    loadPastGames();
  }, [voProvider, loadPastGames]);

  /**
   * Handle retrying to load games on error
   */
  const handleRetry = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  /**
   * Handle loading more games (pagination)
   */
  const handleLoadMore = () => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  };

  /**
   * Handle deleting a game
   */
  const handleDelete = (eventId) => {
    // Go back to list and reload
    handleBackToList();
    loadPastGames();
  };

  /**
   * Handle deleting a game from the list view (by game_common ID)
   */
  const handleDeleteGame = useCallback(async (gameCommonId) => {
    if (!gameCommonId) return;
    try {
      await apiCall(`/games/${gameCommonId}`, { method: 'DELETE' });
      loadPastGames();
    } catch (err) {
      console.error('[PastGamesTab] Failed to delete game:', err);
      toast.error('Failed to delete the game. Please try again.');
    }
  }, [loadPastGames]);

  /**
   * Get game link for sharing
   */
  const getGameLink = useCallback((eventId) => {
    const baseUrl = getWebOrigin();
    const params = new URLSearchParams();
    params.set('tab', 'past-games');
    params.set('eventId', eventId.toString());

    const event = pastGames?.find(e => e.id === eventId);
    if (event?.game?.id) {
      params.set('gameId', event.game.id.toString());
    }

    return `${baseUrl}/squads/${groupId}?${params.toString()}`;
  }, [groupId, pastGames]);

  /**
   * Copy game link to clipboard
   */
  const handleCopyLink = useCallback(() => {
    if (selectedGame) {
      const link = getGameLink(selectedGame.id);
      navigator.clipboard.writeText(link).then(() => {
      }).catch(err => {
        console.error('Failed to copy link:', err);
      });
    }
  }, [selectedGame, getGameLink]);

  // LIST VIEW: Show games and/or completed tournaments owned by this squad
  if (!selectedGame) {
    // Wait for both lists before deciding whether to show a toggle, to avoid
    // it popping in/switching under the user once tournaments finish loading.
    const bothFetched = gamesFetched && tournamentsFetched;
    const hasTournaments = pastTournaments.length > 0;
    const showToggle = bothFetched && pastGames.length > 0 && hasTournaments;
    const effectiveSubTab = showToggle
      ? (activeSubTab || 'games')
      : (bothFetched && hasTournaments ? 'tournaments' : 'games');

    return (
      <div>
        {!bothFetched ? (
          <div className="flex justify-center py-16">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {showToggle && (
              <div className={`inline-flex p-1 rounded-xl border ${theme.border} ${theme.backgroundSecondary} mb-4`}>
                <button
                  onClick={() => setActiveSubTab('games')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    effectiveSubTab === 'games' ? theme.buttonPrimary : `${theme.textMuted} hover:opacity-80`
                  }`}
                >
                  <Gamepad2 className="w-4 h-4" />
                  Games
                </button>
                <button
                  onClick={() => setActiveSubTab('tournaments')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    effectiveSubTab === 'tournaments' ? theme.buttonPrimary : `${theme.textMuted} hover:opacity-80`
                  }`}
                >
                  <Trophy className="w-4 h-4" />
                  Tournaments
                </button>
              </div>
            )}

            {effectiveSubTab === 'tournaments' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pastTournaments.map(tournament => (
                  <TournamentCard
                    key={tournament.id}
                    tournament={tournament}
                    onView={handleTournamentClick}
                  />
                ))}
              </div>
            ) : (
              <GamesList
                games={pastGames}
                loading={loading}
                error={error}
                onRetry={handleRetry}
                emptyTitle="No past games"
                emptyMessage="This squad hasn't played any games yet. Schedule an event and start a game to see game history here."
                pagination={pagination}
                onLoadMore={handleLoadMore}
                onGameClick={handleGameClick}
                onDeleteGame={handleDeleteGame}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
              />
            )}
          </>
        )}
      </div>
    );
  }

  // DETAIL VIEW: Show selected game scoring
  const gameCommonId = selectedGame?.game?.id || null;
  const isGameCompleted = selectedGame?.game?.status === 'completed';
  const sportName = group?.sportType?.sportName || getSportNameSync(sportTypeId);


  if (!gameCommonId) {
    console.error('[PastGamesTab] No gameCommonId found in selectedGame.game.id - selectedGame:', selectedGame);
  }

  const onGameComplete = async (result) => {
    const eventIdToUse = typeof result === 'object' ? selectedGameEvent?.id : result;
    try {
      await apiCall(`/events/${eventIdToUse}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'completed' }),
      });
      if (selectedGameEvent && selectedGameEvent.id === eventIdToUse) {
        setSelectedGameEvent({ ...selectedGameEvent, status: 'completed' });
      }
      loadPastGames();
    } catch (error) {
      console.error('[PastGamesTab] Failed to update event status:', error);
    }
  };

  return (
    <div>
      <button
        onClick={handleBackToList}
        className={`flex items-center gap-x-2 mb-4 px-4 py-2 rounded-lg border ${theme.border} ${theme.backgroundSecondary} ${theme.text} hover:opacity-80 transition-opacity`}
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Past Games</span>
      </button>

      {isGameCompleted ? (
        ScoringComponent === undefined ? (
          <div className="flex justify-center py-12">
            <Loader className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : !ScoringComponent ? (
          <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
            <div className="text-center py-12">
              <p className={`text-lg ${theme.text} mb-4`}>
                Scoring card not available for this sport
              </p>
            </div>
          </div>
        ) : (
          <ScoringComponent
            gameCommonId={gameCommonId}
            event={selectedGameEvent}
            groupId={groupId}
            members={members}
            onBack={handleBackToList}
            onClose={handleBackToList}
            onDelete={handleDelete}
            onGameComplete={onGameComplete}
            onCopyLink={handleCopyLink}
            getShareLink={getGameLink}
          />
        )
      ) : (
        // Non-completed games go through GameComponent so its status→step routing
        // lands the user on formation/scoring instead of the bare ScoringComponent
        // (which renders a slate when there are no players or scores yet).
        <GameComponent
          sportType={sportName?.toLowerCase()}
          sportTypeId={sportTypeId}
          gameCommonId={gameCommonId}
          groupId={groupId}
          members={members}
          event={selectedGameEvent}
          isScorer={selectedGame?.game?.scorerId == currentUserId}
          isAdmin={isAdmin}
          onGameComplete={onGameComplete}
          onCancel={handleBackToList}
        />
      )}
    </div>
  );
};

export default PastGamesTab;
